import React from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X } from 'lucide-react';
import { CollapsibleRichText } from '../../ui/CollapsibleRichText';

interface SortableNoteItemProps {
  id: string;
  note: string;
  index: number;
  onDelete: (index: number) => void;
}

const SortableNoteItem: React.FC<SortableNoteItemProps> = ({ id, note, index, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      className="rounded-lg border-l-4 p-4 shadow-sm transition-all duration-200 hover:shadow-md group"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderLeftColor: 'var(--color-accent)',
        borderColor: 'var(--color-border)',
        ...style
      }}
    >
      <div className="flex items-start justify-between space-x-4 pt-1 pb-0">
        <div className="flex items-start space-x-3 flex-1">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-opacity-20 hover:bg-gray-400 transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <GripVertical className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <CollapsibleRichText
              content={note}
              style={{ color: 'var(--color-text-primary)' }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => onDelete(index)}
          className="opacity-0 group-hover:opacity-100 p-2 rounded-lg transition-all duration-200 hover:bg-red-100 dark:hover:bg-red-900/20"
          style={{ color: 'var(--color-error)' }}
          title="Delete note"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export interface DraggableNotesListProps {
  notes: string[];
  onReorder: (notes: string[]) => void;
  onDelete: (index: number) => void;
}

export const DraggableNotesList: React.FC<DraggableNotesListProps> = ({
  notes,
  onReorder,
  onDelete,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = notes.findIndex((_, i) => `note-${i}` === active.id);
      const newIndex = notes.findIndex((_, i) => `note-${i}` === over.id);
      const reorderedNotes = arrayMove(notes, oldIndex, newIndex);
      onReorder(reorderedNotes);
    }
  };

  if (notes.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={notes.map((_, i) => `note-${i}`)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {notes.map((note, index) => (
            <SortableNoteItem
              key={`note-${index}`}
              id={`note-${index}`}
              note={note}
              index={index}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

